import { unlock, approve, approveManager, approveSupervisor, approveApprovalGroup, changeSickClaimAmount, createPrintPreview, createBatchPrintPreview, createBatchPrintPreviewAll, getEmployeeTimesheet, updateSupervisor } from "../dataLayer/timesheet";
import {Day} from "../interfaces/EmployeeInterface";
import { PayPeriod } from "../interfaces/PayPeriodInterface";
import { TimesheetType } from "../interfaces/TimesheetInterface";
import { JobStatus } from "../interfaces/V2Interface";

class TimesheetManager {

    // Method to unlock a timesheet based on timesheetID and adminEmployeeID
    async unlockTimesheet(timesheetID: number, adminEmployeeID: number): Promise<any> {
        try {
            // Call the data layer function to unlock the timesheet
            const authData = await unlock(timesheetID, adminEmployeeID);
            console.log("Unlock successful:", authData);
            
            // Return the result of the unlock operation
            return authData;

        } catch (error) {
            // Log and re-throw any errors that occur during the unlock process
            console.error("Error during unlock:", error);
            throw error;
        }
    }

    // Method to approve an approval group based on timesheetID, adminEmployeeID, and requiredApprovalGroupsID
    async approveApprovalGroup(timesheetID: number, adminEmployeeID: number, requiredApprovalGroupsID: number): Promise<any> {
        try {
            const authData = await approveApprovalGroup(timesheetID, adminEmployeeID, requiredApprovalGroupsID);
            console.log("Approval group approve successful:", authData);
            return authData;
        } catch (error) {
            console.error("Error during approval group approval:", error);
            throw error;
        }
    }

    // Method to approve a timesheet based on timesheetID, adminEmployeeID, and approval type
    async approveTimesheet(timesheetID: number, adminEmployeeID: number, type: TimesheetType): Promise<any> {
        try {
            // Call the data layer function to approve the timesheet
            const authData = await approve(timesheetID, adminEmployeeID, type);
            console.log("Approve successful:", authData);
            
            // Return the result of the approval operation
            return authData;

        } catch (error) {
            // Log and re-throw any errors that occur during the approval process
            console.error("Error during approval:", error);
            throw error;
        }
    }

    // Method to approve a manager approval based on timesheetID, adminEmployeeID, and requiredManagerApprovalID
    async approveManagerApproval(timesheetID: number, adminEmployeeID: number, requiredManagerApprovalID: number): Promise<any> {
        try {
            // Call the data layer function to approve the manager approval
            const authData = await approveManager(timesheetID, adminEmployeeID, requiredManagerApprovalID);
            console.log("Manager approve successful:", authData);
            
            // Return the result of the manager approval operation
            return authData;

        } catch (error) {
            // Log and re-throw any errors that occur during the manager approval process
            console.error("Error during manager approval:", error);
            throw error;
        }
    }

    // Method to approve a supervisor approval based on timesheetID, adminEmployeeID, and optional requiredSuperApprovalID
    async approveSupervisorApproval(timesheetID: number, adminEmployeeID: number, requiredSuperApprovalID?: number): Promise<any> {
        try {
            // Call the data layer function to approve the supervisor approval
            const authData = await approveSupervisor(timesheetID, adminEmployeeID, requiredSuperApprovalID);
            console.log("Supervisor approve successful:", authData);
            
            // Return the result of the supervisor approval operation
            return authData;

        } catch (error) {
            // Log and re-throw any errors that occur during the supervisor approval process
            console.error("Error during supervisor approval:", error);
            throw error;
        }
    }

    async updateSickClaimAmount(employeeSickClaimIDs: number[], hourlyRate: number): Promise<any> {
        try {
            const result = await changeSickClaimAmount(employeeSickClaimIDs, hourlyRate);
            console.log("Update sick claim amount successful:", result);
            return result;
        } catch (error) {
            console.error("Error updating sick claim amount:", error);
            throw error;
        }
    }

    async openPrintPreview(employeeID: number, payPeriodID: number, altID: string, payPeriod: PayPeriod): Promise<any> {
        try {
            const result = await createPrintPreview(employeeID, payPeriodID, altID, payPeriod);
            console.log("Print successful:", result);
            return result;
        } catch (error) {
            console.error("Error Printing:", error);
            throw error;
        }
    }

    async openBatchPrintPreview(supervisorID: number, payPeriodID: number, altSupervisorID: string, payPeriodObj: any): Promise<any> {
        try {
            const result = await createBatchPrintPreview(supervisorID, payPeriodID, altSupervisorID, payPeriodObj);
            console.log("Batch print successful:", result);
            return result;
        } catch (error) {
            console.error("Error in batch printing:", error);
            throw error;
        }
    }

    async openBatchPrintPreviewAll(
        payPeriodID: number,
        payPeriodObj: any,
        signal?: AbortSignal,
        onProgress?: (jobStatus: JobStatus) => void,
        locationID?: number,
        locationLabel?: string
    ): Promise<any> {
        try {
            const result = await createBatchPrintPreviewAll(payPeriodID, payPeriodObj, signal, onProgress, locationID, locationLabel);
            console.log("Batch ALL print successful:", result);
            return result;
        } catch (error) {
            console.error("Error in batch ALL printing:", error);
            throw error;
        }
    }

    async refreshTimesheet(employeeID: number, payPeriodID: number): Promise<Day[]> {
        try {
            const result = await getEmployeeTimesheet(employeeID, payPeriodID);
            console.log("Refresh successful:", result);
            return result;
        } catch (error) {
            console.error("Error Printing:", error);
            throw error;
        }
    }

    // Method to update supervisor for a timesheet
    async updateTimesheetSupervisor(timesheetID: number, supervisorID: number, adminEmployeeID: number): Promise<any> {
        try {
            // Call the data layer function to update the supervisor
            const authData = await updateSupervisor(timesheetID, supervisorID, adminEmployeeID);
            console.log("Update supervisor successful:", authData);
            
            // Return the result of the supervisor update operation
            return authData;

        } catch (error) {
            // Log and re-throw any errors that occur during the supervisor update process
            console.error("Error during supervisor update:", error);
            throw error;
        }
    }

}

// Exporting an instance of TimesheetManager to use throughout the application
export default new TimesheetManager();
